interface PerformanceMetric {
    name: string;
    value: number;
    timestamp: number;
    type: 'api' | 'render' | 'user_action' | 'error';
    metadata?: Record<string, any>;
}

interface PerformanceStats {
    average: number;
    min: number;
    max: number;
    count: number;
    p95: number;
    p99: number;
}

class PerformanceMonitor {
    private metrics: Map<string, PerformanceMetric[]> = new Map();
    private observers: PerformanceObserver[] = [];
    private isEnabled: boolean = true;

    constructor() {
        this.initializeObservers();
    }

    private initializeObservers(): void {
        if (typeof window === 'undefined') return;

        // Observe navigation timing
        if ('PerformanceObserver' in window) {
            try {
                const navObserver = new PerformanceObserver((list) => {
                    for (const entry of list.getEntries()) {
                        if (entry.entryType === 'navigation') {
                            this.recordMetric('page_load', entry.duration, 'render', {
                                type: entry.entryType,
                                name: entry.name
                            });
                        }
                    }
                });
                navObserver.observe({ entryTypes: ['navigation'] });
                this.observers.push(navObserver);
            } catch (error) {
                console.warn('Navigation timing observer not supported:', error);
            }

            // Observe resource timing
            try {
                const resourceObserver = new PerformanceObserver((list) => {
                    for (const entry of list.getEntries()) {
                        if (entry.entryType === 'resource') {
                            this.recordMetric('resource_load', entry.duration, 'api', {
                                name: entry.name,
                                initiatorType: (entry as any).initiatorType
                            });
                        }
                    }
                });
                resourceObserver.observe({ entryTypes: ['resource'] });
                this.observers.push(resourceObserver);
            } catch (error) {
                console.warn('Resource timing observer not supported:', error);
            }
        }
    }

    recordMetric(name: string, value: number, type: PerformanceMetric['type'], metadata?: Record<string, any>): void {
        if (!this.isEnabled) return;

        const metric: PerformanceMetric = {
            name,
            value,
            timestamp: Date.now(),
            type,
            metadata
        };

        if (!this.metrics.has(name)) {
            this.metrics.set(name, []);
        }

        this.metrics.get(name)!.push(metric);

        // Keep only last 100 metrics per name
        const metrics = this.metrics.get(name)!;
        if (metrics.length > 100) {
            metrics.splice(0, metrics.length - 100);
        }

        console.log(`📊 Performance metric: ${name} = ${value.toFixed(2)}ms`);
    }

    measureApiCall = async <T>(name: string, fn: () => Promise<T>, metadata?: Record<string, any>): Promise<T> => {
        const start = performance.now();
        try {
            const result = await fn();
            const duration = performance.now() - start;
            this.recordMetric(name, duration, 'api', metadata);
            return result;
        } catch (error) {
            const duration = performance.now() - start;
            this.recordMetric(`${name}_error`, duration, 'error', { 
                ...metadata, 
                error: error instanceof Error ? error.message : 'Unknown error' 
            });
            throw error;
        }
    };

    measureUserAction = (name: string, fn: () => void, metadata?: Record<string, any>): void => {
        const start = performance.now();
        try {
            fn();
            const duration = performance.now() - start;
            this.recordMetric(name, duration, 'user_action', metadata);
        } catch (error) {
            const duration = performance.now() - start;
            this.recordMetric(`${name}_error`, duration, 'error', { 
                ...metadata, 
                error: error instanceof Error ? error.message : 'Unknown error' 
            });
            throw error;
        }
    };

    measureRender = (name: string, fn: () => void, metadata?: Record<string, any>): void => {
        const start = performance.now();
        try {
            fn();
            const duration = performance.now() - start;
            this.recordMetric(name, duration, 'render', metadata);
        } catch (error) {
            const duration = performance.now() - start;
            this.recordMetric(`${name}_error`, duration, 'error', { 
                ...metadata, 
                error: error instanceof Error ? error.message : 'Unknown error' 
            });
            throw error;
        }
    };

    getStats(name: string): PerformanceStats | null {
        const metrics = this.metrics.get(name);
        if (!metrics || metrics.length === 0) return null;

        const values = metrics.map(m => m.value).sort((a, b) => a - b);
        const count = values.length;
        const sum = values.reduce((a, b) => a + b, 0);

        return {
            average: sum / count,
            min: values[0],
            max: values[count - 1],
            count,
            p95: values[Math.floor(count * 0.95)],
            p99: values[Math.floor(count * 0.99)]
        };
    }

    getAllStats(): Record<string, PerformanceStats> {
        const stats: Record<string, PerformanceStats> = {};
        
        for (const [name] of Array.from(this.metrics.entries())) {
            const stat = this.getStats(name);
            if (stat) {
                stats[name] = stat;
            }
        }
        
        return stats;
    }

    getSlowestMetrics(limit: number = 10): Array<{ name: string; average: number }> {
        const allStats = this.getAllStats();
        return Object.entries(allStats)
            .map(([name, stats]) => ({ name, average: stats.average }))
            .sort((a, b) => b.average - a.average)
            .slice(0, limit);
    }

    getErrorRate(): number {
        const errorMetrics = Array.from(this.metrics.entries())
            .filter(([name]) => name.includes('_error'));
        
        const totalErrors = errorMetrics.reduce((sum, [, metrics]) => sum + metrics.length, 0);
        const totalMetrics = Array.from(this.metrics.values())
            .reduce((sum, metrics) => sum + metrics.length, 0);
        
        return totalMetrics > 0 ? totalErrors / totalMetrics : 0;
    }

    exportMetrics(): Record<string, PerformanceMetric[]> {
        const exportData: Record<string, PerformanceMetric[]> = {};
        for (const [name, metrics] of Array.from(this.metrics.entries())) {
            exportData[name] = [...metrics];
        }
        return exportData;
    }

    clearMetrics(): void {
        this.metrics.clear();
        console.log('🧹 Performance metrics cleared');
    }

    setEnabled(enabled: boolean): void {
        this.isEnabled = enabled;
        console.log(`📊 Performance monitoring ${enabled ? 'enabled' : 'disabled'}`);
    }

    destroy(): void {
        this.observers.forEach(observer => observer.disconnect());
        this.observers = [];
        this.metrics.clear();
    }
}

// Global performance monitor instance
export const performanceMonitor = new PerformanceMonitor();

// Utility functions for common measurements
export const measureApiCall = <T>(
    name: string, 
    fn: () => Promise<T>, 
    metadata?: Record<string, any>
): Promise<T> => {
    return performanceMonitor.measureApiCall(name, fn, metadata);
};

export const measureUserAction = (
    name: string, 
    fn: () => void, 
    metadata?: Record<string, any>
): void => {
    performanceMonitor.measureUserAction(name, fn, metadata);
};

export const measureRender = (
    name: string, 
    fn: () => void, 
    metadata?: Record<string, any>
): void => {
    performanceMonitor.measureRender(name, fn, metadata);
};

export default PerformanceMonitor;
